import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';

const officeAddress = "Near main Hanuman nagar, Post office gali ke bagal gali main road no=2, dwarkapuri.\nPatna, Bihar 800020";
const officePhone = '9852891115';

const Contact = () => {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = encodeURIComponent(`Name: ${name}\nMessage: ${message}`);
    const url = `https://wa.me/${officePhone}?text=${text}`;
    window.open(url, '_blank');
    setSuccess(true);
    setName('');
    setMessage('');
  };

  return (
    <div className="bg-white dark:bg-secondary-900 min-h-screen pt-12 pb-24">
      {/* Header */}
      <div className="text-center mb-16 px-4">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-6 gradient-text"
        >
          Get in Touch
        </motion.h1>
        <p className="text-xl text-secondary-600 dark:text-secondary-400 max-w-2xl mx-auto">
          Reach out to us for any inquiries or to discuss your security needs.
        </p>
      </div>

      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-secondary-50 dark:bg-secondary-800 p-8 rounded-2xl border border-secondary-100 dark:border-secondary-700"
            >
              <h3 className="text-2xl font-bold mb-6 text-secondary-900 dark:text-white">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-secondary-900 dark:text-white">Head Office</h4>
                    <p className="text-secondary-600 dark:text-secondary-400 whitespace-pre-line leading-relaxed">
                      Devdoot Intelligence & Securities Pvt Ltd<br />
                      {officeAddress}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-secondary-900 dark:text-white">Phone</h4>
                    <a href={`tel:${officePhone}`} className="text-secondary-600 dark:text-secondary-400 hover:text-primary-500 transition-colors text-lg">
                      +91 {officePhone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-secondary-900 dark:text-white">Email</h4>
                    <a href="mailto:info@devdootsecurity.com" className="text-secondary-600 dark:text-secondary-400 hover:text-primary-500 transition-colors text-lg">
                      info@devdootsecurity.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="h-80 rounded-2xl overflow-hidden shadow-lg border border-secondary-200 dark:border-secondary-700"
            >
              <iframe
                title="DIAS Office Location"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src="https://www.google.com/maps?q=25.5900105,85.1622441&z=17&hl=en&output=embed"
                allowFullScreen
              ></iframe>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-secondary-800 p-8 rounded-2xl shadow-xl border border-secondary-200 dark:border-secondary-700 h-fit"
          >
            <h3 className="text-2xl font-bold mb-2 text-secondary-900 dark:text-white">Send us a Message</h3>
            <p className="text-secondary-500 dark:text-secondary-400 mb-8">We usually respond within 24 hours.</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all placeholder:text-secondary-400"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all placeholder:text-secondary-400"
                  placeholder="Tell us what you need..."
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-primary-500/25 flex items-center justify-center gap-2 group"
              >
                <span>Send via WhatsApp</span>
                <Send size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>

              {success && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg"
                >
                  <CheckCircle size={20} />
                  <p className="font-medium text-sm">WhatsApp opened! Please check the new tab.</p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;